import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RevealContact from './RevealContact';
import BookUnitModal from './BookUnitModal';
import SendWhatsApp from './SendWhatsApp';

const EMPTY_FORM = {
  name: '', phone: '', email: '', source: 'direct',
  channel_partner_id: '', project_id: '', assigned_to: '', attended_by: '',
  notes: '', visit_date: new Date().toISOString().split('T')[0],
  budget_min: '', budget_max: '', bedrooms: '',
  possession_preference: '', referrer_name: '', status: 'new'
};

// Site check-in QR (broker / client) — same points as Site visits; shown only to users who can see them.
const CheckinQr = ({ apiBase }) => {
  const [points, setPoints] = useState([]);
  const [imgs, setImgs] = useState({});
  const [copied, setCopied] = useState(null);
  useEffect(() => {
    const urls = [];
    axios.get(`${apiBase}/site-checkin/points`).then((r) => {
      const act = (r.data || []).filter((p) => p.active);
      setPoints(act);
      act.forEach((p) => axios.get(`${apiBase}/site-checkin/points/${p.id}/qr.png`, { responseType: 'blob' })
        .then((b) => { const u = URL.createObjectURL(b.data); urls.push(u); setImgs((m) => ({ ...m, [p.id]: u })); }).catch(() => {}));
    }).catch(() => {});
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [apiBase]);
  if (!points.length) return null;
  const copy = (p) => { navigator.clipboard?.writeText(p.wa_link).then(() => { setCopied(p.id); setTimeout(() => setCopied(null), 1500); }); };
  return (
    <div className="card" style={{ marginBottom: '24px' }}>
      <h3 style={{ margin: '0 0 4px' }}>Walk-in check-in QR</h3>
      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
        Broker scans the partner QR, client scans the client QR — within 10 minutes of each other the client is locked to that broker.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        {points.map((p) => (
          <div key={p.id} style={{ textAlign: 'center', width: 200 }}>
            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>{p.kind === 'partner' ? 'Channel partner (broker)' : 'Client'}</div>
            {imgs[p.id]
              ? <img src={imgs[p.id]} alt={`${p.label} QR`} style={{ width: 160, height: 160 }} />
              : <div style={{ width: 160, height: 160, background: '#f3f4f6', borderRadius: 8, margin: '0 auto' }} />}
            <div style={{ fontSize: '13px', marginTop: '6px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <a href={p.wa_link} target="_blank" rel="noreferrer">Open link</a>
              <button type="button" onClick={() => copy(p)} style={{ border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '13px' }}>{copied === p.id ? 'Copied' : 'Copy link'}</button>
              {imgs[p.id] && <a href={imgs[p.id]} download={`${p.kind}-checkin-qr.png`}>Download</a>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Walkins = ({ apiBase }) => {
  const navigate = useNavigate();
  const [walkins, setWalkins] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWalkin, setEditingWalkin] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [channelPartners, setCPs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [booking, setBooking] = useState(null);
  const [sendTo, setSendTo] = useState(null);
  const [bookedMsg, setBookedMsg] = useState('');

  useEffect(() => {
    fetchWalkins();
    fetchDashboardData();
    axios.get(`${apiBase}/channel-partners`).then(r => setCPs(r.data || [])).catch(() => {});
    axios.get(`${apiBase}/projects`).then(r => setProjects(r.data || [])).catch(() => {});
    axios.get(`${apiBase}/users`).then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchWalkins(); }, [filterStatus]);

  const fetchWalkins = async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${apiBase}/walkins${params}`);
      setWalkins(response.data);
    } catch (error) {
      console.error('Error fetching walk-ins:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${apiBase}/dashboard/developer`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const openNewWalkin = () => {
    setEditingWalkin(null);
    setFormData({ ...EMPTY_FORM, project_id: projects.length > 0 ? projects[0].id : '' });
    setShowModal(true);
  };

  const handleEdit = (walkin) => {
    setEditingWalkin(walkin);
    setFormData({
      name: walkin.name || '', phone: walkin.phone || '', email: walkin.email || '',
      source: walkin.source || 'direct', channel_partner_id: walkin.channel_partner_id || '',
      project_id: walkin.project_id || '', assigned_to: walkin.assigned_to || '', attended_by: walkin.attended_by || '',
      notes: walkin.notes || '', visit_date: walkin.visit_date ? walkin.visit_date.split('T')[0] : '',
      budget_min: walkin.budget_min || '', budget_max: walkin.budget_max || '',
      bedrooms: walkin.bedrooms || '', possession_preference: walkin.possession_preference || '',
      referrer_name: walkin.referrer_name || '', status: walkin.status || 'new'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWalkin) {
        await axios.put(`${apiBase}/walkins/${editingWalkin.id}`, formData);
      } else {
        await axios.post(`${apiBase}/walkins`, formData);
      }
      setShowModal(false);
      setEditingWalkin(null);
      setFormData({ ...EMPTY_FORM });
      fetchWalkins();
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving walk-in:', error);
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this walk-in record? This cannot be undone.')) return;
    try {
      await axios.delete(`${apiBase}/walkins/${id}`);
      fetchWalkins();
      fetchDashboardData();
    } catch (error) {
      alert('Error deleting: ' + (error.response?.data?.error || error.message));
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(amount);

  if (loading) return <div className="loading">Loading walk-ins...</div>;

  const statusCounts = walkins.reduce((acc, w) => { acc[w.status] = (acc[w.status] || 0) + 1; return acc; }, {});

  return (
    <div>
      <CheckinQr apiBase={apiBase} />
      {/* Stats */}
      {dashboardData?.activities && (
        <div className="card" style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '2px solid #2563eb' }}>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginBottom: 0 }}>
            <div className="stat-card" style={{ background: 'white', border: '1px solid #bfdbfe' }}>
              <div className="stat-label">Total Walk-ins</div>
              <div className="stat-value" style={{ color: '#2563eb' }}>{dashboardData.activities.total_walkins || walkins.length}</div>
            </div>
            <div className="stat-card" style={{ background: 'white', border: '1px solid #bfdbfe' }}>
              <div className="stat-label">Today</div>
              <div className="stat-value" style={{ color: '#10b981' }}>{dashboardData.activities.today_walkins || 0}</div>
            </div>
            <div className="stat-card" style={{ background: 'white', border: '1px solid #bfdbfe' }}>
              <div className="stat-label">This Week</div>
              <div className="stat-value" style={{ color: '#f59e0b' }}>{dashboardData.activities.week_walkins || 0}</div>
            </div>
            {dashboardData?.walkins_conversion && (
              <>
                <div className="stat-card" style={{ background: 'white', border: '1px solid #bfdbfe' }}>
                  <div className="stat-label">Converted</div>
                  <div className="stat-value" style={{ color: '#10b981' }}>{dashboardData.walkins_conversion.converted_to_client || 0}</div>
                  <div className="stat-label" style={{ fontSize: '11px' }}>{dashboardData.walkins_conversion.conversion_to_client_rate || 0}% rate</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Main Table */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 className="card-title" style={{ marginBottom: '4px' }}>Walk-ins Management</h2>
            <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
              Total: <strong>{walkins.length}</strong>
              {Object.entries(statusCounts).map(([s, c]) => (
                <span key={s} style={{ marginLeft: '12px' }}>{s}: <strong>{c}</strong></span>
              ))}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select className="form-input" style={{ width: 'auto', padding: '6px 10px', fontSize: '13px' }}
              value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="interested">Interested</option>
              <option value="visited">Visited</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
            <button className="btn btn-primary" onClick={openNewWalkin} style={{ whiteSpace: 'nowrap' }}>
              + New Walk-in
            </button>
          </div>
        </div>
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Reg #</th>
                <th>Client Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Source</th>
                <th>Channel Partner</th>
                <th>BHK</th>
                <th>Budget</th>
                <th>Project</th>
                <th>Visit Date</th>
                <th>Attended by</th>
                <th>Status</th>
                <th style={{ minWidth: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {walkins.length > 0 ? walkins.map((w) => (
                <tr key={w.id}>
                  <td><span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6366f1', fontWeight: 600 }}>{w.registration_number || '-'}</span></td>
                  <td>
                    <strong>{w.name || '-'}</strong>
                    {w.company_name && <div style={{ fontSize: '11px', color: '#64748b' }}>{w.company_name}</div>}
                  </td>
                  <td>
                    <RevealContact row={w} field="phone" entityType="walkin" apiBase={apiBase} />
                  </td>
                  <td style={{ fontSize: '12px' }}>{w.email || '-'}</td>
                  <td>
                    <span className={`badge ${w.source === 'broker' || w.source === 'channel_partner' ? 'badge-info' : w.source === 'referral' ? 'badge-warning' : 'badge-secondary'}`}>
                      {w.source || 'direct'}
                    </span>
                    {w.referrer_name && <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Ref: {w.referrer_name}</div>}
                  </td>
                  <td>
                    {w.broker_name ? (
                      <div>
                        <div style={{ fontWeight: '500', color: '#3b82f6' }}>{w.broker_name}</div>
                        {w.broker_company && <small style={{ color: '#64748b', fontSize: '11px' }}>{w.broker_company}</small>}
                      </div>
                    ) : w.channel_partner_id ? (
                      <span className="badge badge-info">CP #{w.channel_partner_id}</span>
                    ) : (
                      <span style={{ color: '#10b981', fontWeight: 500, fontSize: '12px' }}>Direct</span>
                    )}
                  </td>
                  <td>{w.bedrooms || w.bhk_interest || '-'}</td>
                  <td style={{ fontSize: '12px' }}>
                    {w.budget_min || w.budget_max ? (
                      <span>{w.budget_min ? `₹${formatCurrency(w.budget_min)}` : ''}{w.budget_max ? ` - ₹${formatCurrency(w.budget_max)}` : ''}</span>
                    ) : w.budget ? w.budget : '-'}
                  </td>
                  <td>{w.project_name || '-'}</td>
                  <td>{w.visit_date ? new Date(w.visit_date).toLocaleDateString('en-IN') : '-'}</td>
                  <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{w.attended_by_name || '-'}</td>
                  <td>
                    <span className={`badge badge-${getStatusColor(w.status)}`} style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>{w.status}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {w.status !== 'converted' && (
                        <button onClick={() => setBooking(w)} title="Book a flat for this walk-in" style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #86efac', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>🏠 Book unit</button>
                      )}
                      {w.client_id && (
                        <button onClick={() => setSendTo([{ id: w.client_id, name: w.name }])} title="Send WhatsApp" style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #86efac', background: '#f0fdf4', color: '#15803d', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>💬 Send</button>
                      )}
                      <button onClick={() => handleEdit(w)} title="Edit" style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f8fafc', color: '#1e293b', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}>✏️ Edit</button>
                      <button onClick={() => handleDelete(w.id)} title="Delete" style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}>🗑️</button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="13" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No walk-ins found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {sendTo && <SendWhatsApp apiBase={apiBase} clients={sendTo} onClose={() => setSendTo(null)} />}
      {booking && (
        <BookUnitModal apiBase={apiBase} walkin={booking} onClose={() => setBooking(null)}
          onBooked={(r) => { setBooking(null); setBookedMsg(`Booked ${r.sales.map((s) => s.flat).join(', ')} — client, broker, walk-in and flat are now linked.`); fetchWalkins(); setTimeout(() => setBookedMsg(''), 6000); }} />
      )}
      {bookedMsg && <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1001, background: '#065f46', color: '#fff', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', maxWidth: '420px' }}>{bookedMsg}</div>}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingWalkin ? `Edit Walk-in #${editingWalkin.registration_number || editingWalkin.id}` : 'New Walk-in'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Client Name *</label>
                  <input type="text" className="form-input" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Full name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone *</label>
                  <input type="tel" className="form-input" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Visit Date</label>
                  <input type="date" className="form-input" value={formData.visit_date} onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Source</label>
                  <select className="form-input" value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })}>
                    <option value="direct">Direct Walk-in</option>
                    <option value="channel_partner">Channel Partner</option>
                    <option value="broker">Broker</option>
                    <option value="referral">Referral</option>
                    <option value="online">Online Lead</option>
                    <option value="newspaper">Newspaper</option>
                    <option value="hoarding">Hoarding</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {(formData.source === 'channel_partner' || formData.source === 'broker') && (
                  <div className="form-group">
                    <label className="form-label">Channel Partner</label>
                    <select className="form-input" value={formData.channel_partner_id} onChange={(e) => setFormData({ ...formData, channel_partner_id: e.target.value })}>
                      <option value="">-- Select CP --</option>
                      {channelPartners.map(cp => <option key={cp.id} value={cp.id}>{cp.name} {cp.company_name ? `(${cp.company_name})` : ''}</option>)}
                    </select>
                  </div>
                )}
                {formData.source === 'referral' && (
                  <div className="form-group">
                    <label className="form-label">Referrer Name</label>
                    <input type="text" className="form-input" value={formData.referrer_name} onChange={(e) => setFormData({ ...formData, referrer_name: e.target.value })} placeholder="Who referred?" />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Project</label>
                  <select className="form-input" value={formData.project_id} onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}>
                    <option value="">-- Select Project --</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">BHK Interest</label>
                  <select className="form-input" value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}>
                    <option value="">-- Select --</option>
                    <option value="1BHK">1 BHK</option>
                    <option value="2BHK">2 BHK</option>
                    <option value="2BHK+Study">2 BHK + Study</option>
                    <option value="3BHK">3 BHK</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Budget Min (₹)</label>
                  <input type="number" className="form-input" value={formData.budget_min} onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })} placeholder="e.g. 10000000" />
                </div>
                <div className="form-group">
                  <label className="form-label">Budget Max (₹)</label>
                  <input type="number" className="form-input" value={formData.budget_max} onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })} placeholder="e.g. 25000000" />
                </div>
                <div className="form-group">
                  <label className="form-label">Possession Preference</label>
                  <select className="form-input" value={formData.possession_preference} onChange={(e) => setFormData({ ...formData, possession_preference: e.target.value })}>
                    <option value="">-- Select --</option>
                    <option value="immediate">Immediate</option>
                    <option value="6months">Within 6 months</option>
                    <option value="1year">Within 1 year</option>
                    <option value="2years">1-2 years</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Attended by</label>
                  <select className="form-input" value={formData.attended_by} onChange={(e) => setFormData({ ...formData, attended_by: e.target.value })}>
                    <option value="">Sales Manager (default)</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  <select className="form-input" value={formData.assigned_to} onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}>
                    <option value="">-- Select --</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-input" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="interested">Interested</option>
                    <option value="visited">Visited</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows="3" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Feedback, requirements, comments..." />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  {editingWalkin ? 'Update Walk-in' : 'Register Walk-in'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = { 'new': 'info', 'contacted': 'warning', 'interested': 'info', 'visited': 'success', 'converted': 'success', 'lost': 'danger' };
  return colors[status] || 'info';
};

export default Walkins;
